// DS row 121 PermissionMatrix: the master permission matrix (O-23, G §16), read-only. A dense table whose head and first
// column stay in view while it scrolls inside its card: one row per capability, the eight role columns in the matrix's
// order (HM · Recruiter · Coordinator · HRBP · Comp · Interviewer · TA Mgr · Admin, DC-228 as amended 12 Oct 05:29;
// the BRD's order with the dropped columns removed and the Admin last, cockpit ruling 12 Oct 06:10),
// each cell his access word on a tag; *No* and *None* are drawn in --text-3. A cell he leaves out of a row ("all others
// No") is drawn *No*. The Admin column reads *Admin* on most rows (his access word; DC-228 lets the bare word stand as a
// column head and a cell).

import { Table, Tag, Td, Th, Tr } from "../core";
import { ACCESS_LABELS, ROLE_LABELS, SEATS, type AccessWord, type SeatKey } from "../labels";
import { cx } from "../lib/cx";
import { G6_LABELS } from "./labels";

/** The column heads: his matrix abbreviations, in his order. */
const SEAT_HEADS: Readonly<Record<SeatKey, string>> = {
  hm: ROLE_LABELS.hmAbbr, recruiter: ROLE_LABELS.recruiter, coordinator: ROLE_LABELS.coordinator, hrbp: ROLE_LABELS.hrbpAbbr,
  compensation: ROLE_LABELS.compensationAbbr, interviewer: ROLE_LABELS.interviewer, taManager: ROLE_LABELS.taManagerAbbr,
  admin: ROLE_LABELS.admin,
};

export interface PermissionRow {
  /** The capability's words (a label: *Create request*, *Debrief decision* …). */
  capability: string;
  /** The access word per seat; a seat left out is *No*. */
  cells: Readonly<Partial<Record<SeatKey, AccessWord>>>;
}

export interface PermissionMatrixProps {
  rows: readonly PermissionRow[];
  /** The matrix's name (default *Master permission matrix*). */
  label?: string | undefined;
  /** A max height in px: the matrix scrolls both ways inside its card. */
  scroll?: number | undefined;
  className?: string | undefined;
}

const QUIET: ReadonlySet<string> = new Set([ACCESS_LABELS.No, ACCESS_LABELS.None]);

export function PermissionMatrix({ rows, label = G6_LABELS.permissionMatrix, scroll, className }: PermissionMatrixProps) {
  return (
    <Table
      dense
      stickyHead
      stickyFirst
      scroll={scroll}
      label={label}
      className={cx("mc-mx", className)}
      head={<Tr><Th>{G6_LABELS.capabilities}</Th>{SEATS.map((seat) => <Th key={seat} align="c">{SEAT_HEADS[seat]}</Th>)}</Tr>}
    >
      {rows.map((row) => (
        <Tr key={row.capability}>
          <Th row>{row.capability}</Th>
          {SEATS.map((seat) => {
            const word = row.cells[seat] ?? ACCESS_LABELS.No;
            return (
              <Td key={seat} align="c" label={SEAT_HEADS[seat]}>
                <Tag className={QUIET.has(word) ? "mc-mx-no" : undefined}>{word}</Tag>
              </Td>
            );
          })}
        </Tr>
      ))}
    </Table>
  );
}
